import { useRef, useState } from "preact/hooks"
import { Download, FileUp } from "lucide-react"

import { Button } from "../../../components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { toast } from "../../../components/ui/use-toast"
import { exactCurrencyFormatter } from "../../../utils"
import * as ForecastIO from "../api/forecast_io_api"
import { PlannedCellsTable } from "./planned_cells_table"

interface ForecastImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  version_id: string | null
  version_name: string | null
  /** Called after the import is written; the caller reloads the grid from the server. */
  on_applied: () => void | Promise<void>
}

/**
 * Item 25: export a view, edit it in Excel, load it back. The file is the Shipment Forecast
 * export's layout (customer, product_line, sku, upc, period, …); the server previews first and
 * writes only when every row is clean.
 */
export function ForecastImportDialog(props: ForecastImportDialogProps) {
  const { open, onOpenChange, version_id, version_name, on_applied } = props
  const [file, set_file] = useState<File | null>(null)
  const [preview, set_preview] = useState<ForecastIO.ImportPreviewResponse | null>(null)
  const [busy, set_busy] = useState<"preview" | "apply" | null>(null)
  const [error, set_error] = useState<string | null>(null)
  const input_ref = useRef<HTMLInputElement>(null)

  function reset(): void {
    set_file(null)
    set_preview(null)
    set_error(null)
    if (input_ref.current) input_ref.current.value = ""
  }

  function handle_open_change(next: boolean): void {
    if (!next) reset()
    onOpenChange(next)
  }

  async function handle_template(): Promise<void> {
    if (!version_id) return
    try {
      const blob = await ForecastIO.download_import_template(version_id)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = "forecast-import-template.csv"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      toast({ title: "Template download failed", description: err instanceof Error ? err.message : String(err), variant: "destructive" })
    }
  }

  function handle_file_change(event: Event): void {
    const target = event.currentTarget as HTMLInputElement
    const picked = target.files && target.files.length > 0 ? target.files[0] : null
    set_file(picked)
    set_preview(null)
    set_error(null)
    if (picked && version_id) {
      void run(picked, true)
    }
  }

  async function run(picked: File, dry_run: boolean): Promise<void> {
    if (!version_id) return
    set_busy(dry_run ? "preview" : "apply")
    set_error(null)
    try {
      const result = await ForecastIO.import_forecast(version_id, picked, dry_run)
      set_preview(result)
      if (result.applied) {
        toast({
          title: "Forecast imported",
          description: `${result.cells.length.toLocaleString()} row${result.cells.length === 1 ? "" : "s"} written to ${version_name ?? "this version"}${
            result.new_node_count > 0 ? `, ${result.new_node_count} new row${result.new_node_count === 1 ? "" : "s"} added to the tree` : ""
          }.`,
        })
        handle_open_change(false)
        await on_applied()
      }
    } catch (err) {
      set_error(err instanceof Error ? err.message : String(err))
    } finally {
      set_busy(null)
    }
  }

  const can_apply = file != null && preview != null && !preview.applied && preview.refused.length === 0 && preview.cells.length > 0 && busy == null
  const total_dollars = preview ? preview.cells.reduce((sum: number, cell: ForecastIO.PlannedCell) => sum + Math.round(Number(cell.volume_dollars) * 100), 0) / 100 : 0

  return (
    <Dialog open={open} onOpenChange={handle_open_change}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Import forecast rows</DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file in the Shipment Forecast export's layout. Rows land on the matching customer / product
            line / SKU and period as forecast; a SKU this version does not have yet is added. Blank cells leave the existing
            figure alone. Nothing is written while any row is refused.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" size="sm" onClick={() => void handle_template()} disabled={!version_id}>
            <Download className="mr-1.5 h-4 w-4" />
            Download template
          </Button>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium hover:bg-gray-50">
            <FileUp className="h-4 w-4" />
            {file ? file.name : "Choose CSV or XLSX…"}
            <input ref={input_ref} type="file" accept=".csv,.xlsx,.xlsm,text/csv" className="hidden" onChange={handle_file_change} />
          </label>
          {busy === "preview" ? <span className="text-xs text-gray-500">Checking the file…</span> : null}
        </div>

        {error ? <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

        {preview ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4 text-sm">
              <Stat label="Rows read" value={preview.rows_parsed.toLocaleString()} />
              <Stat label="On existing SKUs" value={preview.rows_matched.toLocaleString()} />
              <Stat label="Adding to the tree" value={`${preview.rows_creating_nodes.toLocaleString()} rows · ${preview.new_node_count} new`} />
              <Stat label="Refused" value={preview.refused.length.toLocaleString()} tone={preview.refused.length > 0 ? "bad" : undefined} />
              <Stat label="Total" value={exactCurrencyFormatter(total_dollars)} />
            </div>
            {preview.refused.length > 0 ? (
              <div className="max-h-48 overflow-auto rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="mb-1 font-semibold">Fix these lines and upload again — the file is written all or nothing.</p>
                <ul className="space-y-0.5">
                  {preview.refused.map((refusal: ForecastIO.ImportRefusal) => (
                    <li key={`${refusal.line}-${refusal.reason}`}>
                      Line {refusal.line}: {refusal.reason}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {preview.cells.length > 0 ? <PlannedCellsTable cells={preview.cells} show_line /> : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => handle_open_change(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!can_apply} onClick={() => file && void run(file, false)}>
            {busy === "apply" ? "Importing…" : `Import ${preview?.cells.length ?? 0} row${preview?.cells.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Stat(props: { label: string; value: string; tone?: "bad" }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{props.label}</div>
      <div className={`font-semibold tabular-nums ${props.tone === "bad" ? "text-red-700" : "text-gray-900"}`}>{props.value}</div>
    </div>
  )
}
